import {
	type CommonFormSchema,
	COMMON_FIELD_NAME,
	COMMON_FREETEXT_FIELDS,
} from '../common/constants';

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_WHO = {
	MUNICIPAL_SERVICES: 'municipalServices',
	SPECIALIST_HEALTH_SERVICES: 'specialistHealthServices',
	EMERGENCY_SERVICES: 'emergencyServices',
	TEST_ANALYSIS_OR_LABORATORY: 'testAnalysisOrLaboratory',
	NON_PROFIT_OR_VOLUNTARY_ACTOR: 'nonProfitOrVoluntaryActor',
} as const;

// NOTE: Used in zod.enum
export const WHO_VALUES = [
	FIELD_VALUE_WHO.MUNICIPAL_SERVICES,
	FIELD_VALUE_WHO.SPECIALIST_HEALTH_SERVICES,
	FIELD_VALUE_WHO.EMERGENCY_SERVICES,
	FIELD_VALUE_WHO.TEST_ANALYSIS_OR_LABORATORY,
	FIELD_VALUE_WHO.NON_PROFIT_OR_VOLUNTARY_ACTOR,
] as const;

export type Who = typeof WHO_VALUES[number];


export const SERVICES_WHERE = {
	OTHER: 'other',
} as const;

export type ServicesWhere = typeof SERVICES_WHERE[keyof typeof SERVICES_WHERE];


export const WHEN_LEGEND_ID = 'whenLegend';

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_REACTIONS = {
	OVERDOSE: 'overdose',
	RESPIRATORY_ARREST: 'respiratoryArrest',
	CARDIAC_ARREST: 'cardiacArrest',
	UNCONSCIOUS_OR_NOT_CONTACTABLE: 'unconsciousOrNotContactable',
	CONVULSIVE_SEIZURES: 'convulsiveSeizures',
	OTHER: 'other',
} as const;

// NOTE: Used in zod.enum
export const REACTIONS_VALUES = [
	FIELD_VALUE_REACTIONS.OVERDOSE,
	FIELD_VALUE_REACTIONS.RESPIRATORY_ARREST,
	FIELD_VALUE_REACTIONS.CARDIAC_ARREST,
	FIELD_VALUE_REACTIONS.UNCONSCIOUS_OR_NOT_CONTACTABLE,
	FIELD_VALUE_REACTIONS.CONVULSIVE_SEIZURES,
	FIELD_VALUE_REACTIONS.OTHER,
] as const;

export type Reaction = typeof REACTIONS_VALUES[number];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_NUMBER_OF_PEOPLE_GOT_REACTIONS = {
	ONE: 'one',
	SEVERAL: 'several',
	UNKNOWN: 'unknown',
} as const;

// NOTE: Used in zod.enum
export const NUMBER_OF_PEOPLE_GOT_REACTIONS_VALUES = [
	FIELD_VALUE_NUMBER_OF_PEOPLE_GOT_REACTIONS.ONE,
	FIELD_VALUE_NUMBER_OF_PEOPLE_GOT_REACTIONS.SEVERAL,
	FIELD_VALUE_NUMBER_OF_PEOPLE_GOT_REACTIONS.UNKNOWN,
] as const;

export type NumberOfPeopleGotReactions = typeof NUMBER_OF_PEOPLE_GOT_REACTIONS_VALUES[number];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_ANALYZED = {
	YES: 'yes',
	NO: 'no',
	UNKNOWN: 'unknown',
} as const;

// NOTE: Used in zod.enum
export const ANALYZED_VALUES = [
	FIELD_VALUE_ANALYZED.YES,
	FIELD_VALUE_ANALYZED.NO,
	FIELD_VALUE_ANALYZED.UNKNOWN,
] as const;

export type Analyzed = typeof ANALYZED_VALUES[number];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_ANALYZED_HOW = {
	QUICK_TEST: 'quickTest',
	ADVANCED: 'advanced',
	OTHER: 'other',
} as const;

// NOTE: Used in zod.enum
export const ANALYZED_HOW_VALUES = [
	FIELD_VALUE_ANALYZED_HOW.QUICK_TEST,
	FIELD_VALUE_ANALYZED_HOW.ADVANCED,
	FIELD_VALUE_ANALYZED_HOW.OTHER,
] as const;

export type AnalyzedHow = typeof ANALYZED_HOW_VALUES[number];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enum.
export const FIELD_VALUE_IF_NALOXONE_WORKED = {
	YES: 'yes',
	NO: 'no',
	NALOXONE_NOT_GIVEN: 'naloxoneNotGiven',
} as const;

// NOTE: Used in zod.enum
export const IF_NALOXONE_WORKED_VALUES = [
	FIELD_VALUE_IF_NALOXONE_WORKED.YES,
	FIELD_VALUE_IF_NALOXONE_WORKED.NO,
	FIELD_VALUE_IF_NALOXONE_WORKED.NALOXONE_NOT_GIVEN,
] as const;

export type IfNaloxoneWorked = typeof IF_NALOXONE_WORKED_VALUES[number];

// One of multiple predefined values.
// Can also be union of literals.
export const SERVICES_ENUM_FIELD_NAME = {
	ANALYZED: 'analyzed',
	ANALYZED_HOW: 'analyzedHow',
	IF_NALOXONE_WORKED: 'ifNaloxoneWorked',
	NUMBER_OF_PEOPLE_GOT_REACTIONS: 'numberOfPeopleGotReactions',
	REACTIONS: 'reactions',
	WHO: 'who',
} as const;

export const SERVICES_DATE_FIELD_NAME = {
	WHEN_DID_THE_INCIDENT_HAPPEN: 'whenDidTheIncidentHappen',
} as const;

export const SERVICES_DYNAMIC_FIELD_NAME = {
	WHERE: 'where', // counties, other
} as const;

export const SERVICES_DYNAMIC_FIELDS = [
	SERVICES_DYNAMIC_FIELD_NAME.WHERE,
] as const;
export type ServicesDynamicFields = typeof SERVICES_DYNAMIC_FIELDS;

export const SERVICES_FREETEXT_FIELD_NAME = {
	ANALYZED_RESULT: 'analyzedResult',
	EMAIL: 'email',
	MOBILE_NUMBER: 'mobileNumber',
	NUMBER_OF_PEOPLE_GOT_REACTIONS_OTHER: 'numberOfPeopleGotReactionsOther',
	REACTIONS_OTHER: 'reactionsOther',
	WHAT_AGENCY_INSTITUTION_OR_ORGANIZATION: 'whatAgencyInstitutionOrOrganization',
	WHEN_DID_THE_INCIDENT_HAPPEN_OTHER: 'whenDidTheIncidentHappenOther',
} as const;

export const SERVICES_FREETEXT_FIELDS = [
	...COMMON_FREETEXT_FIELDS,
	SERVICES_FREETEXT_FIELD_NAME.ANALYZED_RESULT,
	SERVICES_FREETEXT_FIELD_NAME.EMAIL,
	SERVICES_FREETEXT_FIELD_NAME.MOBILE_NUMBER,
	SERVICES_FREETEXT_FIELD_NAME.NUMBER_OF_PEOPLE_GOT_REACTIONS_OTHER,
	SERVICES_FREETEXT_FIELD_NAME.REACTIONS_OTHER,
	SERVICES_FREETEXT_FIELD_NAME.WHAT_AGENCY_INSTITUTION_OR_ORGANIZATION,
	SERVICES_FREETEXT_FIELD_NAME.WHEN_DID_THE_INCIDENT_HAPPEN_OTHER,
] as const;
export type ServicesFreetextFields = typeof SERVICES_FREETEXT_FIELDS;

export const SERVICES_FIELD_NAME = {
	...COMMON_FIELD_NAME,
	...SERVICES_ENUM_FIELD_NAME,
	...SERVICES_DATE_FIELD_NAME,
	...SERVICES_DYNAMIC_FIELD_NAME,
	...SERVICES_FREETEXT_FIELD_NAME,
} as const;

// This should "mostly" match the zodSchema and JSON.schema.
export interface ServicesFormSchema extends CommonFormSchema {
	[SERVICES_FIELD_NAME.ANALYZED]?: Analyzed;
	[SERVICES_FIELD_NAME.ANALYZED_HOW]?: AnalyzedHow;
	[SERVICES_FIELD_NAME.ANALYZED_RESULT]?: string;
	[SERVICES_FIELD_NAME.EMAIL]?: string;
	[SERVICES_FIELD_NAME.IF_NALOXONE_WORKED]?: IfNaloxoneWorked;
	[SERVICES_FIELD_NAME.MOBILE_NUMBER]?: string;
	[SERVICES_FIELD_NAME.NUMBER_OF_PEOPLE_GOT_REACTIONS]?: NumberOfPeopleGotReactions;
	[SERVICES_FIELD_NAME.NUMBER_OF_PEOPLE_GOT_REACTIONS_OTHER]?: string;
	[SERVICES_FIELD_NAME.REACTIONS_OTHER]?: string;
	[SERVICES_FIELD_NAME.REACTIONS]?: Reaction[];
	[SERVICES_FIELD_NAME.TYPE]: 'services';
	[SERVICES_FIELD_NAME.WHAT_AGENCY_INSTITUTION_OR_ORGANIZATION]?: string;
	[SERVICES_FIELD_NAME.WHEN_DID_THE_INCIDENT_HAPPEN_OTHER]?: string;
	[SERVICES_FIELD_NAME.WHEN_DID_THE_INCIDENT_HAPPEN]?: Date;
	[SERVICES_FIELD_NAME.WHERE]: string;
	[SERVICES_FIELD_NAME.WHO]: Who;
}
